import type { ComponentPropsWithoutRef } from 'react';

import { clsxm } from '@vs-shop/utils';
import { type VariantProps, cva } from 'class-variance-authority';

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

// Maps the semantic level to its element tag without an `as` cast (rule 03):
// `satisfies` keeps each value as its literal string, so `tagByLevel[level]`
// narrows to the `HeadingTag` union and is usable directly as a JSX element.
const tagByLevel = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
  6: 'h6',
} satisfies Record<HeadingLevel, HeadingTag>;

// Each level reads the type-scale tokens in `@vs-shop/ui`'s `styles.css`, which
// a tenant theme re-points. Size and weight are separate utilities, so a call
// site's own size keeps the level's weight. Levels 5 and 6 have no style of
// their own and read the surrounding copy.
const headingVariants = cva(['font-heading', 'text-foreground inline tracking-tight'], {
  variants: {
    level: {
      1: 'text-(length:--text-h1)/(--text-h1--line-height) font-(weight:--text-h1--font-weight)',
      2: 'text-(length:--text-h2)/(--text-h2--line-height) font-(weight:--text-h2--font-weight)',
      3: 'text-(length:--text-h3)/(--text-h3--line-height) font-(weight:--text-h3--font-weight)',
      4: 'text-(length:--text-h4)/(--text-h4--line-height) font-(weight:--text-h4--font-weight)',
      5: '',
      6: '',
    },
    hover: {
      true: 'hover:underline',
      false: '',
    },
  },
  defaultVariants: { hover: false },
});

// `level` is required (it picks the element), so it is intersected back as a
// required key on top of VariantProps' optional one.
export type HeadingProps = ComponentPropsWithoutRef<HeadingTag> &
  VariantProps<typeof headingVariants> & { level: HeadingLevel };

export function Heading({ className, level, hover, ...props }: HeadingProps) {
  const Element = tagByLevel[level];

  return (
    <Element
      data-slot="heading"
      className={clsxm(headingVariants({ level, hover }), className)}
      {...props}
    />
  );
}

export { headingVariants };
